import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { employeeApi, type DailyReportHistoryItem } from '../api/client';
import WeeklyReportHistory from '../components/WeeklyReportHistory';
import { dayHistoryRow, formatDayLabel } from '../utils/dayHistory';

/** EMP-S03 振り返り履歴（API 連携） */
export default function ReportHistory() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [period, setPeriod] = useState<'7' | '30' | '90' | '365'>('7');
  const [list, setList] = useState<DailyReportHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(
    async (cursor?: string | null) => {
      const token = getToken();
      if (!token) {
        setLoading(false);
        return;
      }
      const isLoadMore = Boolean(cursor);
      if (!cursor) setError(null);
      if (isLoadMore) setLoadingMore(true);
      else setLoading(true);
      const to = new Date();
      const from = new Date();
      const days = period === '7' ? 7 : period === '30' ? 30 : period === '90' ? 90 : 365;
      from.setDate(from.getDate() - days);
      const fromStr = from.toISOString().slice(0, 10);
      const toStr = to.toISOString().slice(0, 10);
      try {
        const res = await employeeApi.getReportHistory(token, {
          from: fromStr,
          to: toStr,
          limit: days,
          cursor: cursor ?? undefined,
        });
        const data = res.data ?? [];
        const next = res.nextCursor ?? (res as { next_cursor?: string | null }).next_cursor ?? null;
        const more = res.hasMore ?? (res as { has_more?: boolean }).has_more ?? false;
        setList((prev) => (cursor ? prev.concat(data) : data));
        setNextCursor(next);
        setHasMore(more);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
        if (!cursor) setList([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [getToken, period, t]
  );

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="page report-history-page">
      <h1>{t('history.title')}</h1>

      {/* 週ごとの振り返り（課題 #59 段階 4）。週次が無ければ何も出さない */}
      <WeeklyReportHistory />

      <h2 className="report-history-daily-title">{t('history.dailyTitle')}</h2>
      <div className="report-history-tabs" role="tablist" aria-label={t('history.dailyTitle')}>
        <button
          type="button"
          role="tab"
          aria-selected={period === '7'}
          className={period === '7' ? 'report-history-tab active' : 'report-history-tab'}
          onClick={() => setPeriod('7')}
        >
          {t('history.period7')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={period === '30'}
          className={period === '30' ? 'report-history-tab active' : 'report-history-tab'}
          onClick={() => setPeriod('30')}
        >
          {t('history.period30')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={period === '90'}
          className={period === '90' ? 'report-history-tab active' : 'report-history-tab'}
          onClick={() => setPeriod('90')}
        >
          {t('history.period90')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={period === '365'}
          className={period === '365' ? 'report-history-tab active' : 'report-history-tab'}
          onClick={() => setPeriod('365')}
        >
          {t('history.period365')}
        </button>
      </div>

      {error && (
        <div className="report-history-error" role="alert">
          <p>{error}</p>
          <button type="button" className="dashboard-error-retry" onClick={() => load()}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {loading && (
        <div className="report-history-loading">
          <p>{t('common.loading')}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="report-history-list">
          {list.length === 0 ? (
            <p className="report-history-empty">{t('history.noReports')}</p>
          ) : (
            /*
              1 行は、日付・録音件数と、ダッシュボードの「今日のコンディション傾向」と同じ 5 項目
              （課題 #156）。日付を押すと、その日の結果（指標・前日比・AI コメント・録音）を開く。
              項目が多いので、狭い画面では表だけを横に動かせるようにする（ページ全体ははみ出させない）
            */
            <div className="report-history-table-wrap">
              <table className="report-history-table" data-testid="day-history-table">
                <thead>
                  <tr>
                    <th scope="col">{t('history.date')}</th>
                    <th scope="col">{t('history.voiceCount')}</th>
                    <th scope="col">{t('dashboard.domains.vitality')}</th>
                    <th scope="col">{t('dashboard.domains.tension')}</th>
                    <th scope="col">{t('dashboard.domains.cognition')}</th>
                    <th scope="col">{t('dashboard.domains.cognitionModeLabel')}</th>
                    <th scope="col">{t('dashboard.domains.emotionStability')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item) => {
                    const row = dayHistoryRow(item);
                    const d = row.domains;
                    const num = (v: number | undefined) => (d && typeof v === 'number' ? Math.round(v) : '—');
                    return (
                      <tr key={item.report_date} data-testid="day-history-row">
                        <td>
                          <Link to={`/dashboard/history/${item.report_date}`} className="report-history-day-link">
                            {formatDayLabel(row.date)}
                          </Link>
                        </td>
                        <td>{row.voiceCount != null ? t('history.voiceCountValue', { count: row.voiceCount }) : '—'}</td>
                        <td>{num(d?.vitality)}</td>
                        <td>{num(d?.tension)}</td>
                        <td>{num(d?.cognition)}</td>
                        <td>{d?.cognition_mode ?? '—'}</td>
                        <td>{num(d?.emotion_stability)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {hasMore && nextCursor && (
            <button
              type="button"
              className="report-history-load-more"
              onClick={() => load(nextCursor)}
              disabled={loadingMore}
            >
              {loadingMore ? t('common.loading') : t('history.loadMore')}
            </button>
          )}
        </div>
      )}

      <p className="report-history-back">
        <Link to="/dashboard">{t('common.back')} → {t('employee.dashboard')}</Link>
      </p>
    </div>
  );
}
